import {
  Directive, ElementRef,
  OnChanges, OnDestroy, Output, SimpleChanges,
  EventEmitter, HostBinding, HostListener, Input,
} from '@angular/core';

/**
 * libs
 */
import { Dropdown } from 'bootstrap';
import { Rect } from '@popperjs/core';

/**
 * directives
 */
import { ButtonDirective } from '@sharedDirectives/button/button.directive';

@Directive({
  selector: '[appDropdownButton]',
  hostDirectives: [
    {
      directive: ButtonDirective,
      inputs: [
        'id',
        'variant', 'appearance', 'social',
        'size', 'type', 'disabled', 'active', 'rounded', 'iconOnly'
      ]
    }
  ],
  host: {
    '[class.dropdown-toggle]': 'true',
    '[attr.data-bs-toggle]': 'dataBsToggle'
  }
})
export class DropdownButtonDirective implements OnChanges, OnDestroy {

  //--------------------------------------------------------------------------
  // Event Handling (Host Listeners)
  //--------------------------------------------------------------------------

  /**
   * @event
   * Handles when modal is about to be shown via Bootstrap event.
   * Emits visibleChange as true.
   * @param $event - Bootstrap show.bs.modal event
   */
  @HostListener('show.bs.dropdown', ['$event'])
  public showModal($event: Event) {
    this.visibleChange.emit(true);
  }

  /**
   * @event
   * Handles when modal is completely hidden via Bootstrap event.
   * Emits visibleChange as false.
   * @param $event - Bootstrap hidden.bs.modal event
   */
  @HostListener('hidden.bs.dropdown', ['$event'])
  public hiddenModal($event: Event) {
    this.visibleChange.emit(false);
  }

  /**
   * @description to rerender instance & and detect Menu in dom
   */
  @HostListener('mousedown', ['$event'])
  public detectMenu(event?: MouseEvent) {

    if (this.dropdownMenuElement) {

      if (!this.isDetectMenu) {

        this.isDetectMenu = true;

        this.dataBsToggle = 'dropdown';

        this.reRenderDropdown();

      }

    }
    else this.isDetectMenu = false;

  }

  //--------------------------------------------------------------------------
  // Inputs (Configuration)
  //--------------------------------------------------------------------------
  
  /**
   * @description Dropdown positioning config
   */
  @Input({ alias: 'autoClose' }) public autoClose: boolean | 'inside' | 'outside' = true;

  /**
   * @description Dropdown positioning config
   */
  @Input({ alias: 'offset' }) public offset!: Dropdown.Offset | string | Dropdown.OffsetFunction;

  /**
   * @description Dropdown reference
   */
  @Input({ alias: 'reference' }) public reference: "toggle" | "parent" | Element | Rect = 'toggle'

  /**
   * @description Dropdown button split
   */
  @HostBinding('class.dropdown-toggle-split')
  @Input({ alias: 'split' }) public split: boolean = false;

  /**
   * @description Dropdown button split
   */
  @HostBinding('class.hide-arrow')
  @Input({ alias: 'hideArrow' }) public hideArrow: boolean = false;

  //--------------------------------------------------------------------------
  // Outputs (Events)
  //--------------------------------------------------------------------------

  /**
   * @output Emits when modal visibility changes (two-way binding).
   */
  @Output()
  public visibleChange = new EventEmitter<boolean>();

  /**
   * @description bootstrap target for dropdown
   */
  protected dataBsToggle: string =  "";

  /**
   * @description true if dropdown menu is attached in dom, else false
   */
  protected isDetectMenu: boolean = false;

  /**
   * 
   */
  private dropdownInstance!: Dropdown;

  /**
   * @description return dropdown menu `HTMLUListElement`
   */
  private get dropdownMenuElement(): HTMLElement | null | undefined {
    return this.ElRef.nativeElement.parentElement?.querySelector<HTMLElement>('.dropdown-menu')
  }

  /**
   * 
   * @param ElRef 
   */
  constructor(public ElRef: ElementRef<HTMLButtonElement>) { }

  
  ngOnChanges(changes: SimpleChanges): void {

    if (this.dropdownInstance != null) {
      
      // update options should
      this.dispose()

      // with each inputs change should rerender dropdown
      this.reRenderDropdown();

    }

  }

  /**
   * 
   */
  public reRenderDropdown(): void {
    
    // update options should
    this.dispose();

    // void
    if (this.dropdownMenuElement == null) return;

    // init
    const options: Partial<Dropdown.Options> = {};

    if (this.offset != null)  options['offset'] = this.offset;
    if (this.reference != null)  options['reference'] = this.reference;
    if (this.autoClose != null)  options['autoClose'] = this.autoClose;


    // create instance
    this.dropdownInstance = Dropdown.getOrCreateInstance(this.ElRef.nativeElement, options)

  }

  /**
   * 
   */
  public show(): void {
    this.dropdownInstance?.show();
  }

  /**
   * 
   */
  public hide(): void {
    this.dropdownInstance?.hide();
  }

  /**
   * 
   */
  public update(): void {
    this.dropdownInstance?.update();
  }  

  /**
   * 
   */
  public dispose() {
    this.dropdownInstance?.dispose();
  }

  /**
   * 
   */
  ngOnDestroy(): void {
    this.dispose()
  }

}
